import React, { useRef, useCallback, useEffect } from 'react';
import { InputContainer, InputTextarea, SendButton } from './AIChat.Styled';

export type AIChatInputProps = {
	value: string;
	onChange: (value: string) => void;
	onSend: (value: string) => void;
	placeholder?: string;
	isSending?: boolean;
	maxInputRows?: number;
};

const DEFAULT_LINE_HEIGHT_PX = 21;

export function AIChatInput({
	value,
	onChange,
	onSend,
	placeholder = 'Write your reply...',
	isSending = false,
	maxInputRows = 5,
}: AIChatInputProps): React.ReactElement {
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const resize = useCallback(() => {
		const el = textareaRef.current;
		if (!el) return;
		el.style.height = 'auto';
		const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || DEFAULT_LINE_HEIGHT_PX;
		const maxHeight = maxInputRows * lineHeight;
		const newHeight = Math.min(el.scrollHeight, maxHeight);
		el.style.height = newHeight + 'px';
		el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
	}, [maxInputRows]);

	useEffect(() => {
		resize();
	}, [value, resize]);

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		onChange(e.target.value);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
			e.preventDefault();
			if (!isSending && value.trim()) {
				onSend(value);
				onChange('');
			}
		}
	};

	const handleSendClick = () => {
		if (!isSending && value.trim()) {
			onSend(value);
			onChange('');
		}
	};

	const canSend = !isSending && value.trim().length > 0;

	return (
		<InputContainer>
			<InputTextarea
				ref={textareaRef}
				value={value}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				placeholder={placeholder}
				disabled={isSending}
				rows={1}
				aria-label={placeholder}
				aria-multiline={true}
			/>
			<SendButton
				variant="dimmed"
				icon="send"
				size="compact"
				onClick={handleSendClick}
				disabled={!canSend}
				aria-label="Send message"
			/>
		</InputContainer>
	);
}
